
type Context =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'entity'
  | 'organization'
  | 'product'
  | 'resource'
  | 'id'
  | 'jobNum'
  | 'partNum'
  | 'description';

const UINT32_RANGE = 2 ** 32;
const INT32_SIGN_BIT = 2 ** 31;

const toUint32 = (value: number): number =>
  ((Math.trunc(value) % UINT32_RANGE) + UINT32_RANGE) % UINT32_RANGE;

const toInt32 = (value: number): number => {
  const unsigned = toUint32(value);
  return unsigned >= INT32_SIGN_BIT ? unsigned - UINT32_RANGE : unsigned;
};

const unsignedRightShift = (value: number, bits: number): number =>
  Math.floor(toUint32(value) / 2 ** bits);

const xorUint32 = (leftValue: number, rightValue: number): number => {
  let left = toUint32(leftValue);
  let right = toUint32(rightValue);
  let result = 0;
  let place = 1;

  for (let index = 0; index < 32; index += 1) {
    if (left % 2 !== right % 2) {
      result += place;
    }
    left = Math.floor(left / 2);
    right = Math.floor(right / 2);
    place *= 2;
  }

  return toUint32(result);
};

const orUint32 = (leftValue: number, rightValue: number): number => {
  let left = toUint32(leftValue);
  let right = toUint32(rightValue);
  let result = 0;
  let place = 1;

  for (let index = 0; index < 32; index += 1) {
    if (left % 2 === 1 || right % 2 === 1) {
      result += place;
    }
    left = Math.floor(left / 2);
    right = Math.floor(right / 2);
    place *= 2;
  }

  return toUint32(result);
};

const bitwiseOrOne = (value: number): number => {
  const integer = toInt32(value);
  return Math.abs(integer % 2) === 1 ? integer : integer + 1;
};

function makeRng(seed: number): () => number {
  let a = toUint32(seed);
  return () => {
    a = toInt32(a);
    a = toInt32(a + 0x6d2b79f5);
    let t = Math.imul(
      xorUint32(a, unsignedRightShift(a, 15)),
      bitwiseOrOne(a)
    );
    t = xorUint32(
      t + Math.imul(
        xorUint32(t, unsignedRightShift(t, 7)),
        orUint32(61, t)
      ),
      t
    );
    return xorUint32(t, unsignedRightShift(t, 14)) / UINT32_RANGE;
  };
}

const FIRST_NAMES = [
  'Alex', 'Jordan', 'Taylor', 'Morgan', 'Casey', 'Riley', 'Jamie', 'Avery',
  'Quinn', 'Drew', 'Sam', 'Charlie', 'Skyler', 'Reese', 'Cameron', 'Devon',
];
const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Wilson', 'Anderson',
  'Thomas', 'Moore',
];
const COMPANY_PREFIX = [
  'Apex', 'Vertex', 'Summit', 'Pioneer', 'Keystone', 'Nova', 'Onyx', 'Atlas',
  'Cardinal', 'Meridian', 'Sterling', 'Titan',
];
const COMPANY_SUFFIX = ['Industries', 'Manufacturing', 'Group', 'Corp', 'Systems', 'Works'];
const PLANES = [
  'Boeing 737', 'Boeing 747', 'Airbus A320', 'Airbus A380', 'Embraer E175',
  'Bombardier CRJ900', 'Cessna 172', 'Gulfstream G650',
];
const ADJECTIVES = ['Ergonomic', 'Rustic', 'Sleek', 'Refined', 'Handmade', 'Generic', 'Compact'];
const MATERIALS = ['Steel', 'Wooden', 'Concrete', 'Plastic', 'Cotton', 'Granite', 'Rubber'];
const PRODUCT_NOUNS = ['Chair', 'Table', 'Bracket', 'Gasket', 'Panel', 'Fixture', 'Valve'];

const DIGITS = '0123456789';
const UPPER_ALNUM = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

const pick = <T>(arr: T[], rng: () => number): T =>
  arr[Math.floor(rng() * arr.length)];

const randomString = (
  charset: string,
  length: number,
  rng: () => number
): string =>
  Array.from({ length }, () => charset[Math.floor(rng() * charset.length)]).join('');

export function generateFakeValue(inputString: string, context: Context): string;
export function generateFakeValue(
  inputString: null | undefined,
  context: Context
): null | undefined;
export function generateFakeValue(
  inputString: string | null | undefined,
  context: Context
): string | null | undefined;
export function generateFakeValue(
  inputString: string | null | undefined,
  context: Context
): string | null | undefined {
  if (!inputString) return inputString;

  const seed = inputString
    .split('')
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const rng = makeRng(seed);

  switch (context) {
    case 'firstName':
      return pick(FIRST_NAMES, rng);
    case 'lastName':
      return pick(LAST_NAMES, rng);
    case 'email':
      return `${pick(FIRST_NAMES, rng).toLowerCase()}.${pick(
        LAST_NAMES,
        rng
      ).toLowerCase()}@example.com`;
    case 'entity':
    case 'organization':
      return `${pick(COMPANY_PREFIX, rng)} ${pick(COMPANY_SUFFIX, rng)}`;
    case 'product':
      return pick(PLANES, rng);
    case 'resource':
      return randomString(UPPER_ALNUM, 3, rng);
    case 'id':
      return randomString(DIGITS, 10, rng);
    case 'jobNum':
      return randomString(UPPER_ALNUM, 6, rng);
    case 'partNum':
      return randomString(UPPER_ALNUM, 8, rng).replace(/(.{4})/, '$1-');
    case 'description':
      return `${pick(ADJECTIVES, rng)} ${pick(MATERIALS, rng)} ${pick(
        PRODUCT_NOUNS,
        rng
      )}`;
    default:
      throw new Error('Invalid context provided');
  }
}

function replaceByKeyName<
  TKey extends string,
  TObj extends Partial<Record<TKey, string | null | undefined>>
>(
  obj: TObj,
  key: TKey,
  context: Context
): TObj {
  Reflect.set(obj, key, generateFakeValue(obj[key], context));
  return obj;
}

export default replaceByKeyName;
